/**
 * Painel do cliente (CRM 360): tudo que o ERP sabe, sem sair da conversa.
 * Cliente nova: cadastra no ERP aqui mesmo (sem duplicar — o ERP devolve o
 * cadastro existente pelo telefone ou CPF). O ERP continua dono dos dados.
 */
import type {
  ClienteErp,
  ConversaResumo,
  EtapaConversa,
  PedidoResumoErp,
  ResumoComercialErp,
  StatusPedidoChat,
} from "@ydchat/shared";
import {
  ROTULO_ETAPA,
  ROTULO_MOTIVO,
  ROTULO_TIPO_ATRIBUICAO,
  formatarTelefone,
  veOperacaoInteira,
} from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  CalendarClock,
  Cake,
  Clock,
  Link2,
  Link2Off,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Search,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Avatar } from "@/components/ui/avatar";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Esqueleto } from "@/components/ui/esqueleto";
import { PainelLateral } from "@/components/ui/painel-lateral";
import { Selo } from "@/components/ui/selo";
import { api, ErroApi } from "@/lib/api";
import { useAtribuicoes } from "@/lib/equipe";
import { formatarData, formatarHora, formatarMoeda } from "@/lib/formatar";
import { useUsuario } from "@/lib/sessao";
import { FormCliente } from "./form-cliente";

export interface ContextoContato {
  contato: {
    id: string;
    nome: string | null;
    nomePerfil: string | null;
    telefone: string;
    erpClienteId: string | null;
  };
  integrado: boolean;
  novoCliente: boolean;
  cliente: ClienteErp | null;
  vendedoraErp: string | null;
  resumo: ResumoComercialErp | null;
  erpErro: string | null;
  sincronizacaoPendente: boolean;
  podeVerDocumento: boolean;
}

export const STATUS_PEDIDO: Record<
  StatusPedidoChat,
  { rotulo: string; tom: "neutro" | "dourado" | "sucesso" | "alerta" | "perigo" }
> = {
  iniciado: { rotulo: "Pedido iniciado", tom: "neutro" },
  aguardando_pagamento: { rotulo: "Aguardando pagamento", tom: "alerta" },
  pago: { rotulo: "Pago", tom: "sucesso" },
  em_separacao: { rotulo: "Em separação", tom: "dourado" },
  enviado: { rotulo: "Enviado", tom: "dourado" },
  entregue: { rotulo: "Entregue", tom: "sucesso" },
  cancelado: { rotulo: "Cancelado", tom: "perigo" },
};

export function ContextoCliente({
  conversa,
  onFechar,
}: {
  conversa: ConversaResumo;
  onFechar: () => void;
}) {
  const eu = useUsuario();
  const [editando, setEditando] = useState<"cadastrar" | "editar" | "vincular" | null>(null);
  const ctx = useQuery({
    queryKey: ["contexto", conversa.contato.id],
    queryFn: () => api.get<ContextoContato>(`/contatos/${conversa.contato.id}/contexto`),
    staleTime: 60_000,
  });
  const d = ctx.data;
  const cliente = d?.cliente ?? null;
  const pagamentosPendentes =
    d?.resumo?.pedidosPendentes.filter((p) => p.status === "aguardando_pagamento") ?? [];

  return (
    <aside className="flex h-full flex-col bg-white" aria-label="Dados do cliente">
      <div className="flex h-16 items-center justify-between border-b border-neutral-200 px-4">
        <h2 className="text-base">Cliente</h2>
        <div className="flex items-center gap-1">
          {cliente && (
            <Botao
              variante="fantasma"
              tamanho="icone"
              className="size-9"
              onClick={() => setEditando("editar")}
              aria-label="Editar cadastro"
              title="Editar cadastro"
            >
              <Pencil size={16} strokeWidth={1.75} />
            </Botao>
          )}
          <Botao
            variante="fantasma"
            tamanho="icone"
            className="size-9"
            onClick={onFechar}
            aria-label="Fechar painel"
          >
            <X size={18} strokeWidth={1.75} />
          </Botao>
        </div>
      </div>

      <div className="rolagem-fina flex-1 space-y-5 overflow-y-auto p-4">
        <div className="flex items-center gap-3">
          <Avatar nome={cliente?.nome ?? conversa.contato.nome} tamanho="lg" />
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-semibold text-neutral-900">
              {cliente?.nome ?? conversa.contato.nome}
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {d?.novoCliente && <Selo tom="dourado">Novo cliente</Selo>}
              {d?.resumo?.etiquetas.map((e) => (
                <Selo
                  key={e}
                  tom={e === "VIP" ? "dourado" : e === "Bloqueado" ? "perigo" : "neutro"}
                >
                  {e}
                </Selo>
              ))}
              {d?.resumo && d.resumo.quantidadeCompras > 1 && <Selo>Cliente recorrente</Selo>}
            </div>
          </div>
        </div>

        <ul className="space-y-2 text-sm">
          <Linha icone={Phone}>{formatarTelefone(conversa.contato.telefone)}</Linha>
          {cliente?.email && <Linha icone={Mail}>{cliente.email}</Linha>}
          {cliente?.cpfCnpj && (
            <Linha icone={UserRound}>
              {d?.podeVerDocumento ? formatarDocumento(cliente.cpfCnpj) : "CPF cadastrado"}
            </Linha>
          )}
          {cliente?.dataNascimento && (
            <Linha icone={Cake}>{formatarData(`${cliente.dataNascimento}T12:00:00`)}</Linha>
          )}
          {cliente?.endereco && (cliente.endereco.cidade || cliente.endereco.logradouro) && (
            <Linha icone={MapPin}>
              {[
                [cliente.endereco.logradouro, cliente.endereco.numero].filter(Boolean).join(", "),
                cliente.endereco.bairro,
                [cliente.endereco.cidade, cliente.endereco.uf].filter(Boolean).join("/"),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Linha>
          )}
          {d?.vendedoraErp && <Linha icone={UserRound}>Vendedora no ERP: {d.vendedoraErp}</Linha>}
        </ul>

        {d?.sincronizacaoPendente && (
          <p className="flex items-start gap-2 rounded-md bg-gold-50 p-3 text-[13px] text-gold-800">
            <Clock size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            Há um cadastro aguardando o ERP. Ele será enviado assim que o ERP responder.
          </p>
        )}

        {ctx.isPending ? (
          <div className="space-y-2">
            <Esqueleto className="h-16 w-full" />
            <Esqueleto className="h-24 w-full" />
          </div>
        ) : !d ? null : d.erpErro ? (
          <p className="flex items-start gap-2 rounded-md bg-warning-100 p-3 text-[13px] text-warning-500">
            <AlertTriangle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            {d.erpErro}
          </p>
        ) : !d.integrado ? (
          <p className="rounded-md bg-neutral-50 p-3 text-[13px] text-neutral-600">
            Esta loja ainda não está ligada ao ERP.
            {eu.papel === "admin" ? " Configure em Configurações → Integrações." : ""}
          </p>
        ) : d.novoCliente ? (
          !d.sincronizacaoPendente && (
            <div className="rounded-lg border border-dashed border-gold-300 bg-gold-50 p-4 text-sm">
              <p className="font-semibold text-neutral-900">Cliente ainda não cadastrada no ERP</p>
              <p className="mt-1 text-neutral-600">
                Cadastre agora ou ligue a um cadastro que já existe (ex.: ela falava de outro
                número).
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Botao tamanho="sm" onClick={() => setEditando("cadastrar")}>
                  <UserPlus size={15} strokeWidth={1.75} /> Cadastrar
                </Botao>
                <Botao variante="secundario" tamanho="sm" onClick={() => setEditando("vincular")}>
                  <Link2 size={15} strokeWidth={1.75} /> Já é cliente
                </Botao>
              </div>
            </div>
          )
        ) : d.resumo ? (
          <ResumoComercial resumo={d.resumo} pagamentosPendentes={pagamentosPendentes} />
        ) : null}

        <HistoricoAtendimento conversaId={conversa.id} />
        <AtendimentosAnteriores contatoId={conversa.contato.id} conversaAtual={conversa.id} />

        {cliente && veOperacaoInteira(eu.papel) && <Desvincular contatoId={conversa.contato.id} />}
      </div>

      <PainelLateral
        aberto={editando === "cadastrar" || editando === "editar"}
        onFechar={() => setEditando(null)}
        titulo={editando === "editar" ? "Editar cadastro" : "Cadastrar no ERP"}
      >
        {d && (editando === "cadastrar" || editando === "editar") && (
          <FormCliente
            contatoId={conversa.contato.id}
            modo={editando}
            inicial={editando === "editar" ? cliente : null}
            nomeSugerido={conversa.contato.nome}
            podeVerDocumento={d.podeVerDocumento}
            onConcluido={() => setEditando(null)}
          />
        )}
      </PainelLateral>
      <PainelLateral
        aberto={editando === "vincular"}
        onFechar={() => setEditando(null)}
        titulo="Ligar a cadastro existente"
      >
        {editando === "vincular" && (
          <VincularExistente
            contatoId={conversa.contato.id}
            onConcluido={() => setEditando(null)}
          />
        )}
      </PainelLateral>
    </aside>
  );
}

function formatarDocumento(d: string): string {
  const n = d.replace(/\D/g, "");
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return d;
}

function Linha({ icone: Icone, children }: { icone: typeof Phone; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-neutral-700">
      <Icone size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-neutral-400" />
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

function ResumoComercial({
  resumo,
  pagamentosPendentes,
}: {
  resumo: ResumoComercialErp;
  pagamentosPendentes: PedidoResumoErp[];
}) {
  const prefs = resumo.preferencias;
  const temPrefs =
    prefs && (prefs.tamanhos.length || prefs.cores.length || prefs.categorias.length);
  const outrosPendentes = resumo.pedidosPendentes.filter(
    (p) => p.status !== "aguardando_pagamento",
  );
  return (
    <div className="space-y-4">
      {!!pagamentosPendentes.length && (
        <div className="rounded-lg border border-warning-500/30 bg-warning-100/60 p-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-warning-500">
            <CalendarClock size={15} strokeWidth={1.75} />{" "}
            {pagamentosPendentes.length === 1
              ? "Pagamento pendente"
              : `${pagamentosPendentes.length} pagamentos pendentes`}
          </p>
          <ul className="mt-1 space-y-0.5 text-[13px] text-neutral-700">
            {pagamentosPendentes.map((p) => (
              <li key={p.id}>
                Pedido #{p.numero} ·{" "}
                <span className="font-display font-bold">{formatarMoeda(p.total)}</span> ·{" "}
                {formatarData(p.criadoEm)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-2">
        <Metrica rotulo="Compras" valor={String(resumo.quantidadeCompras)} />
        <Metrica rotulo="Total comprado" valor={formatarMoeda(resumo.valorTotal)} />
        <Metrica rotulo="Ticket médio" valor={formatarMoeda(resumo.ticketMedio)} />
        <Metrica
          rotulo="Última compra"
          valor={resumo.ultimaCompraEm ? formatarData(resumo.ultimaCompraEm) : "—"}
        />
      </dl>
      {!!temPrefs && (
        <div>
          <h3 className="mb-1.5 text-sm">Costuma comprar</h3>
          <div className="flex flex-wrap gap-1.5">
            {prefs.categorias.map((c) => (
              <Selo key={`c-${c}`}>{c}</Selo>
            ))}
            {prefs.tamanhos.map((t) => (
              <Selo key={`t-${t}`} tom="dourado">
                Tam. {t}
              </Selo>
            ))}
            {prefs.cores.map((c) => (
              <Selo key={`o-${c}`}>{c}</Selo>
            ))}
          </div>
        </div>
      )}
      {!!outrosPendentes.length && <ListaPedidos titulo="Em andamento" pedidos={outrosPendentes} />}
      {!!resumo.pedidosRecentes.length && (
        <ListaPedidos titulo="Pedidos recentes" pedidos={resumo.pedidosRecentes} />
      )}
    </div>
  );
}

function ListaPedidos({ titulo, pedidos }: { titulo: string; pedidos: PedidoResumoErp[] }) {
  return (
    <div>
      <h3 className="mb-2 text-sm">{titulo}</h3>
      <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
        {pedidos.slice(0, 5).map((p) => {
          const s = STATUS_PEDIDO[p.status];
          return (
            <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm">
              <div>
                <p className="font-semibold text-neutral-900">#{p.numero}</p>
                <p className="text-xs text-neutral-500">
                  {formatarData(p.criadoEm)} · {p.canal}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-display font-bold text-neutral-900">
                  {formatarMoeda(p.total)}
                </span>
                <Selo tom={s.tom}>{s.rotulo}</Selo>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Metrica({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-lg bg-neutral-50 p-3">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.04em] text-neutral-500">
        {rotulo}
      </dt>
      <dd className="mt-0.5 font-display text-lg font-bold text-neutral-900">{valor}</dd>
    </div>
  );
}

/** Quem atendeu esta conversa e por quê (só a equipe vê). */
function HistoricoAtendimento({ conversaId }: { conversaId: string }) {
  const hist = useAtribuicoes(conversaId);
  if (!hist.data?.length) return null;
  return (
    <div>
      <h3 className="mb-2 text-sm">Histórico do atendimento</h3>
      {/* Rolagem própria: um histórico longo não empurra o resto do painel. */}
      <ol className="rolagem-fina max-h-56 space-y-2.5 overflow-y-auto overscroll-contain border-l border-neutral-200 pl-3">
        {hist.data.map((a) => (
          <li key={a.id} className="relative text-[13px]">
            <span className="absolute -left-[17px] top-1.5 size-2 rounded-full bg-gold-400" />
            <p className="text-neutral-800">
              <span className="font-semibold">{ROTULO_TIPO_ATRIBUICAO[a.tipo]}</span>
              {a.para ? ` para ${a.para.nome}` : ""}
              {a.de && a.tipo !== "automatica" ? ` (era ${a.de.nome})` : ""}
            </p>
            <p className="text-xs text-neutral-500">
              {formatarData(a.criadoEm)} {formatarHora(a.criadoEm)}
              {a.por ? ` · por ${a.por.nome}` : ""}
              {a.motivo ? ` · ${ROTULO_MOTIVO[a.motivo] ?? a.motivo}` : ""}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function VincularExistente({
  contatoId,
  onConcluido,
}: {
  contatoId: string;
  onConcluido: () => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [aplicada, setAplicada] = useState("");
  const busca = useQuery({
    queryKey: ["busca-erp", contatoId, aplicada],
    queryFn: () =>
      api.get<ClienteErp[]>(`/contatos/${contatoId}/busca-erp?q=${encodeURIComponent(aplicada)}`),
    enabled: aplicada.length >= 2,
  });
  const vincular = useMutation({
    mutationFn: (erpClienteId: string) =>
      api.post(`/contatos/${contatoId}/vincular`, { erpClienteId }),
    onSuccess: () => {
      toast.success("Cliente ligada ao cadastro do ERP!");
      void qc.invalidateQueries({ queryKey: ["contexto", contatoId] });
      void qc.invalidateQueries({ queryKey: ["conversas"] });
      onConcluido();
    },
    onError: (e) => toast.error(e instanceof ErroApi ? e.message : "Não foi possível ligar."),
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">Busque pelo nome ou pelo CPF completo.</p>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAplicada(q.trim());
        }}
      >
        <Campo
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nome ou CPF"
          autoFocus
        />
        <Botao type="submit" variante="secundario" tamanho="icone" aria-label="Buscar">
          <Search size={17} strokeWidth={1.75} />
        </Botao>
      </form>
      {busca.isFetching ? (
        <Esqueleto className="h-24" />
      ) : busca.isError ? (
        <p className="text-sm text-danger-500">{busca.error.message}</p>
      ) : busca.data && !busca.data.length ? (
        <p className="text-sm text-neutral-500">
          Nenhum cadastro encontrado. Se for cliente nova, use “Cadastrar”.
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
          {busca.data?.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900">{c.nome}</p>
                <p className="text-xs text-neutral-500">
                  {c.telefone ? formatarTelefone(c.telefone) : "Sem telefone"}
                  {c.endereco?.cidade ? ` · ${c.endereco.cidade}` : ""}
                </p>
              </div>
              <Botao
                tamanho="sm"
                onClick={() => vincular.mutate(c.id)}
                disabled={vincular.isPending}
              >
                Ligar
              </Botao>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Desvincular({ contatoId }: { contatoId: string }) {
  const qc = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const m = useMutation({
    mutationFn: () => api.del(`/contatos/${contatoId}/vincular`),
    onSuccess: () => {
      toast.success("Vínculo desfeito.");
      void qc.invalidateQueries({ queryKey: ["contexto", contatoId] });
      void qc.invalidateQueries({ queryKey: ["conversas"] });
    },
  });
  return confirmando ? (
    <div className="rounded-md border border-neutral-200 p-3 text-[13px]">
      <p className="text-neutral-700">
        Desfazer o vínculo com este cadastro do ERP? O cadastro no ERP não muda.
      </p>
      <div className="mt-2 flex gap-2">
        <Botao variante="perigo" tamanho="sm" onClick={() => m.mutate()}>
          Desfazer
        </Botao>
        <Botao variante="fantasma" tamanho="sm" onClick={() => setConfirmando(false)}>
          Cancelar
        </Botao>
      </div>
    </div>
  ) : (
    <button
      onClick={() => setConfirmando(true)}
      className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800"
    >
      <Link2Off size={13} strokeWidth={1.75} /> Cadastro errado? Desfazer vínculo
    </button>
  );
}

interface ConversaAnterior {
  id: string;
  status: string;
  etapa: EtapaConversa;
  criadoEm: string;
  encerradaEm: string | null;
  responsavel: string | null;
  ultimaMensagem: string | null;
  pedido: { numero: number; total: number } | null;
  podeAbrir: boolean;
}

/** Outras conversas desta cliente: quando, com quem, como terminou. */
function AtendimentosAnteriores({
  contatoId,
  conversaAtual,
}: {
  contatoId: string;
  conversaAtual: string;
}) {
  const lista = useQuery({
    queryKey: ["contexto", contatoId, "conversas"],
    queryFn: () => api.get<ConversaAnterior[]>(`/contatos/${contatoId}/conversas`),
    staleTime: 60_000,
  });
  const outras = (lista.data ?? []).filter((c) => c.id !== conversaAtual);
  if (!outras.length) return null;
  return (
    <div>
      <h3 className="mb-2 text-sm">Atendimentos anteriores ({outras.length})</h3>
      <ul className="rolagem-fina max-h-72 space-y-2 overflow-y-auto overscroll-contain pr-1">
        {outras.map((c) => {
          const conteudo = (
            <>
              <p className="flex items-center justify-between gap-2 text-[13px]">
                <span className="font-semibold text-neutral-900">{formatarData(c.criadoEm)}</span>
                <span className="text-xs text-neutral-500">
                  {c.responsavel ?? "sem responsável"}
                </span>
              </p>
              <p className="truncate text-xs text-neutral-500">
                {ROTULO_ETAPA[c.etapa]}
                {c.pedido ? ` · pedido #${c.pedido.numero} (${formatarMoeda(c.pedido.total)})` : ""}
              </p>
              {c.ultimaMensagem && (
                <p className="truncate text-xs text-neutral-600">“{c.ultimaMensagem}”</p>
              )}
            </>
          );
          return (
            <li key={c.id}>
              {c.podeAbrir ? (
                <Link
                  to="/atendimentos/$conversaId"
                  params={{ conversaId: c.id }}
                  search={(s) => s}
                  className="block rounded-lg border border-neutral-200 p-2.5 hover:border-gold-300 hover:bg-gold-50/40"
                >
                  {conteudo}
                </Link>
              ) : (
                <div className="rounded-lg border border-neutral-200 p-2.5">{conteudo}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
